import { MenuIcon, PenIcon, TrashIcon } from "@xola/icons";
import { flash, Label, Table } from "@xola/ui-kit";
import clsx from "clsx";
import { produce } from "immer";
import React from "react";
import {
    DragDropContext,
    Draggable,
    DraggableProvided,
    Droppable,
    DroppableProvided,
    DropResult,
} from "react-beautiful-dnd";
import { EquipmentCatalogItem, Experience, ProductCatalog, UnitType } from "types";
import { AddEquipmentPopover } from "./AddEquipmentPopover";
import { EditEquipmentModal } from "./EditEquipmentModal";
import { InlineConfirmDeletePopover } from "@/components/Common/InlineConfirmDeletePopover";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { useDraggableCatalogItems } from "@/hooks/useDraggableCatalogItems";
import { populateMissingSequences } from "@/models/CatalogItem";

interface EquipmentCatalogEditorProps {
    value: ProductCatalog;
    experience: Experience;
    error?: string;
    onChange: (catalog: ProductCatalog) => void;
}

const getEquipmentTemplate = (item: EquipmentCatalogItem) => item.template?.id ?? null;

const findCatalogItemIndex = (items: EquipmentCatalogItem[], catalogItem: EquipmentCatalogItem) => {
    const templateId = getEquipmentTemplate(catalogItem);
    return templateId === null
        ? items.indexOf(catalogItem)
        : items.findIndex((item) => getEquipmentTemplate(item) === templateId);
};

export const EquipmentCatalogEditor = ({ value, experience, error, onChange }: EquipmentCatalogEditorProps) => {
    const catalogItems = value.items.filter((catalogItem) => {
        return catalogItem.unitType === UnitType.EQUIPMENT;
    });
    const isSequenceMissing = catalogItems.find((catalogItem) => !catalogItem.sequence);
    const equipmentCatalogItems = (
        isSequenceMissing ? populateMissingSequences(catalogItems) : catalogItems
    ) as EquipmentCatalogItem[];

    const handleInsertCatalogItem = (catalogItem: EquipmentCatalogItem) => {
        if (findCatalogItemIndex(equipmentCatalogItems, catalogItem) >= 0) {
            flash.danger("This equipment already exists");
            return;
        }

        /**
         * If a catalog item is deleted and re-added again before saving, we add back the original catalog
         * item so that its id persists instead of creating a new one.
         */
        const deletedCatalogItem = experience.catalog.items.find((item) => {
            return item.template?.id === catalogItem.template?.id;
        });

        const catalog = produce(value, (draft) => {
            draft.items.push(deletedCatalogItem ?? catalogItem);
        });

        onChange(catalog);
    };

    const handleDeleteCatalogItem = (catalogItem: EquipmentCatalogItem) => {
        const catalog = produce(value, (draft) => {
            const index = findCatalogItemIndex(draft.items as EquipmentCatalogItem[], catalogItem);
            if (index >= 0) {
                draft.items.splice(index, 1);
            }
        });
        onChange(catalog);
    };

    const handleUpdateCatalogItem = (catalogItem: EquipmentCatalogItem) => {
        const catalog = produce(value, (draft) => {
            const index = findCatalogItemIndex(draft.items as EquipmentCatalogItem[], catalogItem);
            if (index >= 0) {
                draft.items[index] = catalogItem;
            }
        });

        onChange(catalog);
    };

    return (
        <div className="space-y-2">
            <div className="mb-2 flex items-center justify-between">
                <Label className={error && "!text-danger"}>Equipment {error ? <span>{error}</span> : null}</Label>
                <AddEquipmentPopover onInsert={handleInsertCatalogItem} />
            </div>
            <EquipmentCatalogItemList
                value={value}
                catalogItems={equipmentCatalogItems}
                onChange={onChange}
                onDeleteCatalogItem={handleDeleteCatalogItem}
                onUpdateCatalogItem={handleUpdateCatalogItem}
            />
        </div>
    );
};

interface EquipmentCatalogItemListProps {
    value: ProductCatalog;
    catalogItems: EquipmentCatalogItem[];
    onChange: (catalog: ProductCatalog) => void;
    onDeleteCatalogItem: (catalogItem: EquipmentCatalogItem) => void;
    onUpdateCatalogItem: (catalogItem: EquipmentCatalogItem) => void;
}

export const EquipmentCatalogItemList = ({
    value,
    catalogItems,
    onChange,
    onDeleteCatalogItem,
    onUpdateCatalogItem,
}: EquipmentCatalogItemListProps) => {
    const {
        items: equipmentItems,
        showEditCatalogItemModal,
        selectedCatalogItem,
        toggleEditCatalogItemModal,
        handleSubmit,
        handleDragEnd,
        handleItemDelete,
    } = useDraggableCatalogItems({
        value,
        catalogItems,
        unitType: UnitType.EQUIPMENT,
        onChange,
        onDeleteCatalogItem,
        onUpdateCatalogItem,
    });

    return (
        <>
            <DragDropContext onDragEnd={(result: DropResult) => handleDragEnd(result)}>
                <Droppable
                    droppableId="equipmentTable"
                    renderClone={(provided, _, rubric) => (
                        <EquipmentDragCloneRow
                            provided={provided}
                            item={equipmentItems[rubric.source.index]}
                            index={rubric.source.index}
                            toggleEditCatalogItemModal={toggleEditCatalogItemModal}
                            handleItemDelete={handleItemDelete}
                        />
                    )}
                >
                    {(provided: DroppableProvided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}>
                            <Table>
                                <Table.Head>
                                    <Table.Row>
                                        <Table.Header className="!text-sm" />
                                        <Table.Header className="!text-sm">Equipment</Table.Header>
                                        <Table.Header className="!text-sm">Display Name</Table.Header>
                                        <Table.Header className="!text-sm">Caption</Table.Header>
                                        <Table.Header className="text-right !text-sm">Actions</Table.Header>
                                    </Table.Row>
                                </Table.Head>

                                <Table.Body isStriped={false} className="max-h-51 overflow-y-scroll">
                                    {equipmentItems.length > 0 ? (
                                        <>
                                            {equipmentItems.map((item, index: number) => (
                                                <Draggable key={item.name} draggableId={item.name} index={index}>
                                                    {({ draggableProps, dragHandleProps, innerRef }, snapshot) => (
                                                        <tr
                                                            ref={innerRef}
                                                            {...draggableProps}
                                                            {...dragHandleProps}
                                                            key={item.name}
                                                            className={clsx("justify-between", {
                                                                shadow: snapshot.isDragging,
                                                            })}
                                                            data-testid="equipment-catalog-editor-row"
                                                        >
                                                            <Table.Cell className="w-1/12">
                                                                <span className="inline-flex rounded border border-gray-light px-1 py-0.5">
                                                                    <MenuIcon size="small" />
                                                                </span>
                                                            </Table.Cell>
                                                            <Table.Cell className="w-1/12">
                                                                <NoTranslate>{item.name}</NoTranslate>
                                                            </Table.Cell>
                                                            <Table.Cell className="w-2/12">
                                                                <NoTranslate className="">
                                                                    {item.displayName ?? ""}
                                                                </NoTranslate>
                                                            </Table.Cell>
                                                            <Table.Cell className="w-2/12">
                                                                <NoTranslate className="whitespace-normal break-all">
                                                                    {item.caption ?? ""}
                                                                </NoTranslate>
                                                            </Table.Cell>
                                                            <Table.Cell className="w-2/12 space-x-1 text-right">
                                                                <SecondaryButton
                                                                    className="mb-1"
                                                                    data-testid="equipment-catalog-editor-edit"
                                                                    size="tiny"
                                                                    onClick={(event: any) =>
                                                                        toggleEditCatalogItemModal(event, item)
                                                                    }
                                                                >
                                                                    <PenIcon />
                                                                </SecondaryButton>
                                                                <InlineConfirmDeletePopover
                                                                    onConfirm={() => handleItemDelete(index, item)}
                                                                >
                                                                    <SecondaryButton
                                                                        data-testid="equipment-catalog-editor-remove"
                                                                        className="mb-1"
                                                                        size="tiny"
                                                                    >
                                                                        <TrashIcon />
                                                                    </SecondaryButton>
                                                                </InlineConfirmDeletePopover>
                                                            </Table.Cell>
                                                        </tr>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </>
                                    ) : (
                                        <Table.Row>
                                            <Table.Cell colSpan={5} className="text-center">
                                                No Equipment
                                            </Table.Cell>
                                        </Table.Row>
                                    )}
                                </Table.Body>
                            </Table>
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
            <EditEquipmentModal
                isOpen={showEditCatalogItemModal}
                catalogItem={selectedCatalogItem!}
                onClose={toggleEditCatalogItemModal}
                onSubmit={handleSubmit}
            />
        </>
    );
};

const EquipmentDragCloneRow = ({
    provided,
    item,
    index,
    toggleEditCatalogItemModal,
    handleItemDelete,
}: {
    provided: DraggableProvided;
    item: EquipmentCatalogItem;
    index: number;
    toggleEditCatalogItemModal: (event: any, item: EquipmentCatalogItem) => void;
    handleItemDelete: (index: number, item: EquipmentCatalogItem) => void;
}) => {
    return (
        <tr
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={provided.draggableProps.style}
            className="pointer-events-none bg-white"
        >
            <Table.Cell className="w-1/12">
                <span className="inline-flex rounded border border-gray-light px-2 py-0.5">
                    <MenuIcon size="small" />
                </span>
            </Table.Cell>
            <Table.Cell className="w-9/12">
                <NoTranslate className="whitespace-normal break-all">{item.displayName ?? item.name}</NoTranslate>
            </Table.Cell>
            <Table.Cell className="w-2/12 space-x-1 text-right">
                <SecondaryButton
                    className="mb-1"
                    data-testid="equipment-catalog-editor-edit"
                    size="tiny"
                    onClick={(event: any) => toggleEditCatalogItemModal(event, item)}
                >
                    <PenIcon />
                </SecondaryButton>
                <InlineConfirmDeletePopover onConfirm={() => handleItemDelete(index, item)}>
                    <SecondaryButton data-testid="equipment-catalog-editor-remove" className="mb-1" size="tiny">
                        <TrashIcon />
                    </SecondaryButton>
                </InlineConfirmDeletePopover>
            </Table.Cell>
        </tr>
    );
};
